import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export type ReportMode = "auto" | "every_conversation" | "digest" | "escalation_only" | "off";
export type ReportDigestFrequency = "weekly" | "fortnightly" | "monthly";
export type EscalationSeverity = "low" | "medium" | "high";

export interface EscalationSettings {
  channels?: string[];
  recipients?: { emails?: string[] };
  min_severity?: EscalationSeverity;
}

interface ReportModeSelectorProps {
  reportMode: ReportMode;
  onReportModeChange: (mode: ReportMode) => void;
  digestFrequency: ReportDigestFrequency | null;
  onDigestFrequencyChange: (frequency: ReportDigestFrequency) => void;
  digestDayOfWeek: number | null;
  onDigestDayOfWeekChange: (day: number) => void;
  escalationSettings: EscalationSettings;
  onEscalationSettingsChange: (settings: EscalationSettings) => void;
  hasSlackConnection: boolean;
  /** Per-genie decision prompt used by "auto" mode to decide needs_attention/auto_handled/irrelevant. */
  decisionPrompt: string | null;
  onDecisionPromptChange: (prompt: string) => void;
  /** Clears the prompt back to null so the next auto-decision call regenerates a default. */
  onRegenerateDecisionPrompt: () => void;
  brand?: string;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const MODE_OPTIONS: Array<{ value: ReportMode; label: string; description: string }> = [
  { value: "auto", label: "Auto (AI decides)", description: "AI categorizes every conversation and sends you a daily summary" },
  { value: "every_conversation", label: "Every conversation", description: "Send a report after each conversation" },
  { value: "digest", label: "Scheduled digest", description: "Batch summaries on a recurring schedule" },
  { value: "escalation_only", label: "Only when attention needed", description: "AI decides when a human should look" },
  { value: "off", label: "Off", description: "No reports or alerts sent" },
];

export const ReportModeSelector = ({
  reportMode,
  onReportModeChange,
  digestFrequency,
  onDigestFrequencyChange,
  digestDayOfWeek,
  onDigestDayOfWeekChange,
  escalationSettings,
  onEscalationSettingsChange,
  hasSlackConnection,
  decisionPrompt,
  onDecisionPromptChange,
  onRegenerateDecisionPrompt,
  brand = "#4E9CFF",
}: ReportModeSelectorProps) => {
  const escalationEmails = escalationSettings.recipients?.emails ?? [];
  const escalationChannels = escalationSettings.channels ?? [];

  const setEscalationEmails = (emails: string[]) => {
    onEscalationSettingsChange({ ...escalationSettings, recipients: { ...escalationSettings.recipients, emails } });
  };

  const toggleChannel = (channel: string) => {
    const next = escalationChannels.includes(channel)
      ? escalationChannels.filter((c) => c !== channel)
      : [...escalationChannels, channel];
    onEscalationSettingsChange({ ...escalationSettings, channels: next });
  };

  return (
    <div className="space-y-4">
      <RadioGroup value={reportMode} onValueChange={(v) => onReportModeChange(v as ReportMode)} className="space-y-2">
        {MODE_OPTIONS.map((option) => (
          <label
            key={option.value}
            htmlFor={`report-mode-${option.value}`}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
              reportMode === option.value ? "bg-white shadow-sm" : "bg-gray-50 border-gray-200"
            }`}
            style={reportMode === option.value ? { borderColor: `${brand}60` } : undefined}
          >
            <RadioGroupItem value={option.value} id={`report-mode-${option.value}`} className="mt-0.5" />
            <div>
              <div className="text-sm font-medium text-gray-900">{option.label}</div>
              <div className="text-xs text-gray-500">{option.description}</div>
            </div>
          </label>
        ))}
      </RadioGroup>

      {reportMode === "auto" && (
        <div className="pl-4 border-l-2 space-y-3" style={{ borderColor: `${brand}40` }}>
          <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2.5">
            Conversations that need a human are still emailed to you immediately, just like "Only when attention
            needed" below. Everything else — handled automatically and irrelevant conversations — is rolled up into
            one summary email per day.
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-medium text-gray-700">Decision prompt</Label>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onRegenerateDecisionPrompt}>
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset to default
              </Button>
            </div>
            <Textarea
              value={decisionPrompt ?? ""}
              onChange={(e) => onDecisionPromptChange(e.target.value)}
              placeholder="A default prompt will be generated automatically the first time a conversation is analyzed."
              className="min-h-[120px] text-sm"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Tells the AI what counts as needing your attention vs. routine vs. irrelevant for this genie.
              Customize it for your business — e.g. what a claim, complaint, or high-value lead looks like.
            </p>
          </div>
        </div>
      )}

      {reportMode === "digest" && (
        <div className="pl-4 border-l-2 space-y-3" style={{ borderColor: `${brand}40` }}>
          <div>
            <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Frequency</Label>
            <Select value={digestFrequency ?? undefined} onValueChange={(v) => onDigestFrequencyChange(v as ReportDigestFrequency)}>
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(digestFrequency === "weekly" || digestFrequency === "fortnightly") && (
            <div>
              <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Send on</Label>
              <Select value={String(digestDayOfWeek ?? 1)} onValueChange={(v) => onDigestDayOfWeekChange(Number(v))}>
                <SelectTrigger className="max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {reportMode === "escalation_only" && (
        <div className="pl-4 border-l-2 space-y-4" style={{ borderColor: `${brand}40` }}>
          <div>
            <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Alert channels</Label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={escalationChannels.includes("email")}
                  onChange={() => toggleChannel("email")}
                />
                Email
              </label>
              <label className={`flex items-center gap-2 text-sm ${hasSlackConnection ? "text-gray-700" : "text-gray-400"}`}>
                <input
                  type="checkbox"
                  checked={escalationChannels.includes("slack")}
                  onChange={() => toggleChannel("slack")}
                  disabled={!hasSlackConnection}
                />
                Slack {!hasSlackConnection && "(connect Slack first)"}
              </label>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Minimum severity</Label>
            <Select
              value={escalationSettings.min_severity ?? "low"}
              onValueChange={(v) => onEscalationSettingsChange({ ...escalationSettings, min_severity: v as EscalationSeverity })}
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Escalation recipients</Label>
            <div className="space-y-2">
              {escalationEmails.map((email, index) => (
                <div key={index} className="flex gap-2 min-w-0">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const next = [...escalationEmails];
                      next[index] = e.target.value;
                      setEscalationEmails(next);
                    }}
                    className="flex-1 min-w-0 max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="oncall@example.com"
                  />
                  <button
                    type="button"
                    onClick={() => setEscalationEmails(escalationEmails.filter((_, i) => i !== index))}
                    className="flex items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200 text-sm shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setEscalationEmails([...escalationEmails, ""])}>
                <Plus className="w-4 h-4 mr-2" />
                Add Email
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
